import { Fragment } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  PERFORMING_ASSETS_EYEBROW_SX,
  PERFORMING_ASSETS_HAIRLINE,
} from "./performingAssetsStyles";

const renderPrimary = ({ primary, primaryClamp }) => {
  if (typeof primary !== "string") return primary;

  const clampSx =
    primaryClamp <= 1
      ? {
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }
      : {
          display: "-webkit-box",
          WebkitLineClamp: primaryClamp,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          overflowWrap: "anywhere",
        };

  return (
    <Tooltip title={primary} placement="top-start" arrow>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: "text.primary", ...clampSx }}
      >
        {primary}
      </Typography>
    </Tooltip>
  );
};

const PerformingAssetsLeaderboard = ({
  title,
  columns = [],
  rows = [],
  primaryClamp = 2,
  showRank = true,
  rankOffset = 0,
}) => {
  if (!rows.length || !columns.length) return null;

  const gridTemplateColumns = `minmax(0, 1fr) repeat(${columns.length}, max-content)`;

  return (
    <Box>
      {title ? (
        <Typography
          component="p"
          sx={{ ...PERFORMING_ASSETS_EYEBROW_SX, mb: 1.5 }}
        >
          {title}
        </Typography>
      ) : null}

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns,
            columnGap: 1.5,
            alignItems: "center",
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box />
          {columns.map((column) => (
            <Typography
              key={column.key}
              component="p"
              sx={{
                ...PERFORMING_ASSETS_EYEBROW_SX,
                textAlign: "right",
                whiteSpace: "nowrap",
              }}
            >
              {column.label}
            </Typography>
          ))}

          {rows.map((row, index) => (
            <Fragment key={row.id}>
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  height: "1px",
                  bgcolor: PERFORMING_ASSETS_HAIRLINE,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  minWidth: 0,
                  py: 1.25,
                }}
              >
                {showRank ? (
                  <Typography
                    component="span"
                    sx={{
                      width: 20,
                      flexShrink: 0,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "fields.placeholder",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {String(index + 1 + rankOffset).padStart(2, "0")}
                  </Typography>
                ) : null}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  {renderPrimary({ primary: row.primary, primaryClamp })}
                </Box>
              </Box>
              {columns.map((column) => (
                <Typography
                  key={`${row.id}-${column.key}`}
                  variant="body2"
                  sx={{
                    py: 1.25,
                    textAlign: "right",
                    fontWeight: 500,
                    color: "text.primary",
                    fontVariantNumeric: "tabular-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.metrics?.[column.key] ?? "—"}
                </Typography>
              ))}
            </Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default PerformingAssetsLeaderboard;
